function formatCurrency (value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2
  }).format(value);
}

function cpfValidation (value: any): boolean {
  if (!value) {
    return false;
  }

  const validTypes =
    typeof value === 'string' ||
    Number.isInteger(value) ||
    Array.isArray(value);

  if (!validTypes) {
    return false;
  }

  const numbers = value.toString().match(/\d/g)?.map(Number);

  if (!numbers || numbers.length !== 11) {
    return false;
  }

  const items = [...new Set(numbers)];
  if (items.length === 1) {
    return false;
  }

  const base = numbers.slice(0, 9);
  const digits = numbers.slice(9);

  const calc = (n: number, i: number, x: number) => n * (x - i);
  const sum = (r: number, n: number) => r + n;
  const digit = (n: number) => {
    const rest = n % numbers.length;
    return rest < 2 ? 0 : numbers.length - rest;
  };

  const calc0 = base
    .map((n: number, i: number) => calc(n, i, numbers.length - 1))
    .reduce(sum, 0);
  const digit0 = digit(calc0);

  if (digit0 !== digits[0]) {
    return false;
  }

  const calc1 = base
    .concat(digit0)
    .map((n: number, i: number) => calc(n, i, numbers.length))
    .reduce(sum, 0);
  const digit1 = digit(calc1);

  return digit1 === digits[1];
}

const CNPJ_LENGTH = 14;
const CNPJ_BODY_LENGTH = 12;
const CNPJ_REGEX = /^[A-Z0-9]{12}[0-9]{2}$/;
const CNPJ_FORMAT_REGEX =
  /^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})([0-9]{2})$/;
const ZEROED_CNPJ = '00000000000000';
const CNPJ_CHECK_DIGIT_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

function sanitizeCnpj(cnpjInput: string): string {
  return cnpjInput.replace(/[^A-Za-z0-9]+/g, '').toUpperCase();
}

function calculateMod11CheckDigit(weightedSum: number): number {
  return weightedSum % 11 < 2 ? 0 : 11 - (weightedSum % 11);
}

function calculateCnpjCheckDigits(cnpjBody: string): string {
  let firstCheckDigitWeightedSum = 0;
  let secondCheckDigitWeightedSum = 0;

  for (let characterIndex = 0; characterIndex < CNPJ_BODY_LENGTH; characterIndex++) {
    const characterNumericValue =
      cnpjBody.charCodeAt(characterIndex) - '0'.charCodeAt(0);
    firstCheckDigitWeightedSum +=
      characterNumericValue * CNPJ_CHECK_DIGIT_WEIGHTS[characterIndex + 1];
    secondCheckDigitWeightedSum +=
      characterNumericValue * CNPJ_CHECK_DIGIT_WEIGHTS[characterIndex];
  }

  const firstCheckDigit = calculateMod11CheckDigit(firstCheckDigitWeightedSum);
  secondCheckDigitWeightedSum +=
    firstCheckDigit * CNPJ_CHECK_DIGIT_WEIGHTS[CNPJ_BODY_LENGTH];
  const secondCheckDigit = calculateMod11CheckDigit(secondCheckDigitWeightedSum);

  return `${firstCheckDigit}${secondCheckDigit}`;
}

function cnpjValidation(cnpjInput: string): boolean {
  if (!cnpjInput) {
    return false;
  }

  const sanitizedCnpj = sanitizeCnpj(cnpjInput);

  if (
    sanitizedCnpj.length !== CNPJ_LENGTH ||
    !CNPJ_REGEX.test(sanitizedCnpj) ||
    sanitizedCnpj === ZEROED_CNPJ
  ) {
    return false;
  }

  const cnpjBody = sanitizedCnpj.slice(0, CNPJ_BODY_LENGTH);
  const providedCheckDigits = sanitizedCnpj.slice(CNPJ_BODY_LENGTH);

  return providedCheckDigits === calculateCnpjCheckDigits(cnpjBody);
}

function formatCnpj(cnpjInput: string): string {
  const sanitizedCnpj = sanitizeCnpj(cnpjInput);

  if (sanitizedCnpj.length !== CNPJ_LENGTH) {
    return cnpjInput;
  }

  return sanitizedCnpj.replace(CNPJ_FORMAT_REGEX, '$1.$2.$3/$4-$5');
}

function matchesCnpjSearch(storedCnpj: string, searchQuery: string): boolean {
  return sanitizeCnpj(storedCnpj).includes(sanitizeCnpj(searchQuery));
}

function splitCnpjEntries(cnpjEntriesText: string): string[] {
  return cnpjEntriesText
    .split(/[;\n]/)
    .map(entry => entry.trim())
    .filter(Boolean);
}

function parseCnpjEntries(cnpjEntriesText: string): string[] {
  return splitCnpjEntries(cnpjEntriesText)
    .map(entry => sanitizeCnpj(entry))
    .filter(sanitizedCnpj => cnpjValidation(sanitizedCnpj));
}

function applyMask (value: string, pattern: string): string {
  const regex = new RegExp(createRegex(pattern));
  const groups = createGroupString(pattern);

  return value.replace(regex, groups);
}

function createRegex (pattern: string): string {
  let regexString = '^';

  let currentToken = '';
  let currentTokenType: string | null = null;
  let groupCount = 0;
  let tokenCount = 1;

  for (const char of pattern) {
    const tokenType = getTokenType(char);

    if (tokenType !== currentTokenType) {
      if (currentToken !== '') {
        regexString += `(${currentToken}{${tokenCount - 1}})`;
        groupCount++;
        tokenCount = 0;
      }

      currentToken = '';
      currentTokenType = tokenType;
    }
    tokenCount++;
    currentToken = getRegexToken(char);
  }

  if (currentToken !== '') {
    regexString += `(${currentToken}{${tokenCount - 1}})`;
    groupCount++;
    tokenCount = 0;
  }

  regexString += '$';

  if (groupCount === 0) {
    throw new Error('Invalid pattern. At least one token is required.');
  }

  return `${regexString}`;
}

function createGroupString (pattern: string): string {
  let groupString = '';

  let groupIndex = 1;
  let currentTokenType: string | null = null;

  for (const char of pattern) {
    const tokenType = getTokenType(char);
    if (tokenType !== currentTokenType) {
      currentTokenType = tokenType;
      if (currentTokenType !== null) {
        groupString += `$${groupIndex}`;
        groupIndex++;
      }
    }

    if (tokenType === null) {
      groupString += char;
    }
  }

  return groupString;
}

function getTokenType (char: string): string | null {
  if (char === '#') {
    return 'number';
  } else if (char === 'A') {
    return 'uppercase';
  } else if (char === 'a') {
    return 'lowercase';
  }

  return null;
}

function getRegexToken (char: string): string {
  if (char === '#') {
    return '\\d';
  } else if (char === 'A') {
    return '[A-Z]';
  } else if (char === 'a') {
    return '[a-z]';
  }

  return '';
}

export {
  applyMask,
  cnpjValidation,
  cpfValidation,
  formatCnpj,
  formatCurrency,
  matchesCnpjSearch,
  parseCnpjEntries,
  splitCnpjEntries,
};
